/**
 * [INPUT]: 依赖 AuthProvider menus/signOut、zinnercms-design-system sidebar 原语
 * [OUTPUT]: 对外提供 AppSidebar（Zinner 运营菜单，无环境切换）
 * [POS]: components 全局导航；被 MainLayout 消费
 * [PROTOCOL]: 变更时更新此头部，然后检查 CLAUDE.md
 */

import { useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  CalendarDays,
  ChevronRight,
  LayoutDashboard,
  LogOut,
  Mail,
  MessageSquareText,
  User,
  Users,
} from 'lucide-react'
import { useAuth } from '@/providers/AuthContext'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from '@/zinnercms-design-system/ui/sidebar'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/zinnercms-design-system/ui/collapsible'
import { Button } from '@/zinnercms-design-system/ui/button'

const ICONS = {
  dashboard: LayoutDashboard,
  users: Users,
  user: User,
  calendar: CalendarDays,
  message: MessageSquareText,
  mail: Mail,
}

function iconFor(key) {
  return ICONS[key] || LayoutDashboard
}

export default function AppSidebar() {
  const { menus, user, signOut } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [openGroups, setOpenGroups] = useState(() => ({ zinner: true }))

  const flatLeaves = useMemo(() => {
    const leaves = []
    for (const m of menus) {
      if (m.children?.length) leaves.push(...m.children)
      else if (m.path) leaves.push(m)
    }
    return leaves
  }, [menus])

  const activePath = location.pathname

  return (
    <Sidebar collapsible="icon" className="border-r border-border/80">
      <SidebarHeader className="gap-3 px-3 py-4">
        <button
          type="button"
          className="flex items-center gap-2.5 rounded-xl px-1 text-left"
          onClick={() => navigate('/admin/dashboard')}
        >
          <img src="/zinner-logo.png" alt="Zinner" className="size-8 rounded-full object-cover" />
          <div className="min-w-0 group-data-[collapsible=icon]:hidden">
            <div className="truncate text-sm font-semibold tracking-tight">Zinner CMS</div>
            <div className="truncate text-[11px] text-muted-foreground">运营后台</div>
          </div>
        </button>
      </SidebarHeader>

      <SidebarContent>
        {menus.map((group) => {
          if (!group.children?.length) {
            const Icon = iconFor(group.icon)
            const active = activePath === group.path
            return (
              <SidebarGroup key={group.menuId}>
                <SidebarGroupContent>
                  <SidebarMenu>
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        isActive={active}
                        onClick={() => navigate(group.path)}
                        tooltip={group.menuName}
                      >
                        <Icon />
                        <span>{group.menuName}</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            )
          }

          const open = openGroups[group.menuId] ?? true
          return (
            <Collapsible
              key={group.menuId}
              open={open}
              onOpenChange={(v) => setOpenGroups((s) => ({ ...s, [group.menuId]: v }))}
              className="group/collapsible"
            >
              <SidebarGroup>
                <SidebarGroupLabel asChild>
                  <CollapsibleTrigger className="flex w-full items-center gap-1">
                    <span>{group.menuName}</span>
                    <ChevronRight className="ml-auto size-3.5 transition-transform group-data-[state=open]/collapsible:rotate-90" />
                  </CollapsibleTrigger>
                </SidebarGroupLabel>
                <CollapsibleContent>
                  <SidebarGroupContent>
                    <SidebarMenu>
                      <SidebarMenuItem>
                        <SidebarMenuSub>
                          {group.children.map((child) => {
                            const Icon = iconFor(child.icon)
                            const active = activePath === child.path
                            return (
                              <SidebarMenuSubItem key={child.menuId}>
                                <SidebarMenuSubButton
                                  isActive={active}
                                  onClick={() => navigate(child.path)}
                                >
                                  <Icon />
                                  <span>{child.menuName}</span>
                                </SidebarMenuSubButton>
                              </SidebarMenuSubItem>
                            )
                          })}
                        </SidebarMenuSub>
                      </SidebarMenuItem>
                    </SidebarMenu>
                  </SidebarGroupContent>
                </CollapsibleContent>
              </SidebarGroup>
            </Collapsible>
          )
        })}
      </SidebarContent>

      <SidebarFooter className="gap-2 p-3">
        <div className="truncate px-1 text-xs text-muted-foreground group-data-[collapsible=icon]:hidden">
          {user?.email}
        </div>
        <Button
          variant="outline"
          size="sm"
          className="justify-start"
          onClick={() => signOut()}
        >
          <LogOut className="size-3.5" />
          <span className="group-data-[collapsible=icon]:hidden">退出</span>
        </Button>
        <div className="hidden">{flatLeaves.length}</div>
      </SidebarFooter>
    </Sidebar>
  )
}
